<template>
  <v-data-table :headers="headers"
                :items="filteredIcdCodeProjects"
                :items-per-page="itemsPerPage"
                :group-by="groupBy">
    <template v-slot:top>
      <v-toolbar color="secondary" flat>
        <v-toolbar-title class="text-button">ICD10 Code Library</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-dialog v-model="dialog" max-width="1000px">
          <template v-slot:activator="{ props }">
            <v-btn color="primary" dark class="mb-2" v-bind="props" v-if="this.initialized">New Item</v-btn>
          </template>
          <v-card>
            <v-form lazy-validation @submit.prevent="validateAndSave" ref="icd_code_project_form" :disabled="dialogDisabled">
              <v-card-title>
                <span class="text-h5">{{ dialogFormTitle }}</span>
              </v-card-title>
              <v-card-text class="error" v-if="failedSave">Failed to save item! Please check that there is no
                existing entry with the same ICD10 code.</v-card-text>
              <v-card-text>
                <v-container>
                  <v-row>
                    <v-col cols="12" sm="6" md="4">
                      <v-text-field v-model="getGroup(this.groupItems[0].id).name" :rules="rules.group"
                                    label="Owner company" :disabled="true"
                                    v-if="(editedIndex === -1) && (groupItems.length === 1)">
                      </v-text-field>
                      <v-select v-model="editedItem.group"
                                :rules="rules.group"
                                :items="groupItems"
                                v-else-if="editedIndex === -1"
                                :item-title="(item) => item.name"
                                :item-value="(item) => item.id"
                                label="Owner company" hide-selected small-chips>
                      </v-select>
                      <v-text-field v-model="getGroup(editedItem.group).name" :rules="rules.group" label="Owner company"
                                    :disabled="true" v-else></v-text-field>
                    </v-col>
                    <v-col cols="12">
                      <v-combobox v-model="editedItem.comorbidity_disease_label"
                                      v-model:search="comorbidityDiseaseSearch"
                                      :items="comorbidityDiseaseItems"
                                      :rules="rules.comorbidityDisease"
                                      label="Comorbidity/disease"
                                      placeholder="Select comorbidity/disease"
                                      hint="Select a previously used comorbidity/disease or enter a new one."
                                      chips persistent-hint hide-no-data>
                        <template v-slot:no-data>
                          <v-list-item>
                            <v-list-item-title>
                              No results matching "<strong>{{ comorbidityDiseaseSearch }}</strong>". Press <kbd>enter</kbd> to create a new comorbidity/disease.
                            </v-list-item-title>
                          </v-list-item>
                        </template>
                      </v-combobox>
                    </v-col>
                    <v-col cols="12">
                      <v-autocomplete v-model="editedItem.icd_code"
                                      :items="icdCodeSearchItems"
                                      :rules="rules.icd_code"
                                      :item-title="(item) => item.code + ': ' + item.description"
                                      :item-value="(item) => item.code"
                                      label="ICD10 code"
                                      placeholder="Select ICD10 code"
                                      :hint="dialogFormIcdHint"
                                      :return-object="false"
                                      :multiple="isNewEntry"
                                      clear-on-select persistent-hint hide-no-data>
                      </v-autocomplete>
                    </v-col>
                    <v-col cols="12">
                      <v-text-field v-model="editedItem.remarks" label="Remarks"></v-text-field>
                    </v-col>
                    <v-col cols="12">
                      <v-autocomplete v-model="editedItem.newProjects"
                                      v-model:search="projectSearch"
                                      :items="projectItems"
                                      :rules="rules.projects"
                                      @keydown.enter.prevent.stop.capture="newProject()"
                                      :item-title="(item) => item.number + ': ' + item.name"
                                      :item-value="(item) => item"
                                      label="Projects"
                                      placeholder="Select projects"
                                      hint="Select from previously used projects or enter a new project number."
                                      chips multiple persistent-hint hide-no-data>
                        <template v-slot:no-data>
                          <v-list-item>
                            <v-list-item-title>
                              No results matching "<strong>{{ projectSearch }}</strong>". Press <kbd>enter</kbd> to create a new project.
                            </v-list-item-title>
                          </v-list-item>
                        </template>
                      </v-autocomplete>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="12">
                      <ProgressBar mode="indeterminate" style="height: 4px" :hidden="!dialogDisabled"></ProgressBar>
                    </v-col>
                  </v-row>
                </v-container>
              </v-card-text>
              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn variant="text" @click="close" :disabled="dialogDisabled">Cancel</v-btn>
                <v-btn type="submit" variant="text" :disabled="dialogDisabled">Save</v-btn>
              </v-card-actions>
            </v-form>
          </v-card>
        </v-dialog>
        <v-dialog v-model="dialogDelete" max-width="500px">
          <v-card>
            <v-card-title>Confirm delete</v-card-title>
            <v-card-text class="error" v-if="failedDelete">Failed to delete item!</v-card-text>
            <v-card-text>Are you sure that you want to delete this item?</v-card-text>
            <v-row>
              <v-col cols="12">
                <ProgressBar mode="indeterminate" style="height: 4px" :hidden="!dialogDisabled"></ProgressBar>
              </v-col>
            </v-row>
            <v-card-actions class="mt-10">
              <v-spacer></v-spacer>
              <v-btn variant="text" @click="closeDelete" :disabled="dialogDisabled">Cancel</v-btn>
              <v-btn variant="text" @click="deleteItemConfirm" :disabled="dialogDisabled">OK</v-btn>
              <v-spacer></v-spacer>
            </v-card-actions>
          </v-card>
        </v-dialog>
        <v-dialog v-model="dialogAddNewProject" max-width="500px">
          <v-card>
            <v-form @submit.prevent="validateAndSaveNewProject"
                    ref="add_new_project_form"
                    :disabled="dialogDisabled"
                    lazy-validation>
              <v-card-title>Add new project</v-card-title>
              <v-card-text>
                <v-container>
                  <v-row>
                    <v-col cols="12" sm="6" md="4">
                      <v-text-field v-model="editedItem.newProjectNumber"
                                    label="Project number"
                                    :disabled="true"></v-text-field>
                    </v-col>
                    <v-col cols="12">
                      <v-text-field v-model="editedItem.newProjectName" label="Project name"
                                    :rules="rules.projectName"></v-text-field>
                    </v-col>
                  </v-row>
                </v-container>
              </v-card-text>
              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn variant="text" @click="closeAddNewProject" :disabled="dialogDisabled">Cancel</v-btn>
                <v-btn type="submit" variant="text" :disabled="dialogDisabled">Save</v-btn>
              </v-card-actions>
            </v-form>
          </v-card>
        </v-dialog>
        <v-dialog v-model="dialogDownload" max-width="25%">
          <v-card>
            <v-row>
              <v-col cols="12">
                <ProgressBar mode="indeterminate" style="height: 4px" :hidden="!dialogDisabled"></ProgressBar>
              </v-col>
            </v-row>
          </v-card>
        </v-dialog>
        <v-btn variant="plain" icon="mdi-file-excel" :disabled="!this.initialized || this.dialogDownload"
               @click.stop="downloadExcel"></v-btn>
        <v-btn variant="text" icon="mdi-filter" @click.stop="filter_drawer = !filter_drawer"></v-btn>
      </v-toolbar>
    </template>
    <template v-slot:[`header.data-table-group`]>
      <div>Comorbidity/disease</div>
    </template>
    <template v-slot:[`group-header`]="{ item, columns, toggleGroup, isGroupOpen }">
      <tr>
        <td :colspan="columns.length">
          <v-btn
              :ref="(el) => {
                if (!isGroupOpen(item) && !initialized) {
                  toggleGroup(item);
                }
              }"
              size="small"
              variant="text"
              :icon="isGroupOpen(item) ? '$expand' : '$next'"
              @click="toggleGroup(item)"
          ></v-btn>
          <v-chip class="me-2" color="primary" size="small" :label="true"
                  @click="toggleAllOneComorbidityDisease(item.value)">{{ item.value }}</v-chip>
        </td>
      </tr>
    </template>
    <template v-slot:[`item.icd_code`]="{ item }">
      <v-chip class="me-2" color="primary" size="small" :label="true"
              @click="toggleAllOneIcdCodes(item.icd_code)">{{ item.icd_code }}</v-chip>
    </template>
    <template v-slot:[`item.icd_description`]="{ item }">
      {{ getIcdDescription(item.icd_code) }}
    </template>
    <template v-slot:[`item.created_at`]="{ item }">
      {{ formatTimestamp(item.created_at) }}
    </template>
    <template v-slot:[`item.updated_at`]="{ item }">
      {{ formatTimestamp(item.updated_at) }}
    </template>
    <template v-slot:[`item.projects`]="{ item }">
      <v-chip-group :column="true">
        <v-chip v-for="project in item.projects" :key="project" class="me-2" color="primary" size="small"
                :label="true" @click="toggleAllOneProjects(project)">{{ project.number }}: {{ project.name }}</v-chip>
      </v-chip-group>
    </template>
    <template v-slot:[`item.actions`]="{ item }">
      <v-icon size="small" class="me-2" @click="editItem(item)">mdi-pencil</v-icon>
      <v-icon size="small" @click="deleteItem(item)">mdi-delete</v-icon>
    </template>
    <template v-slot:no-data>
      <v-progress-circular color="primary" v-if="!this.initialized" indeterminate></v-progress-circular>
      <v-card-text v-else>No data available</v-card-text>
    </template>
  </v-data-table>
  <v-navigation-drawer v-model="filter_drawer" location="right" v-bind:width="500">
    <v-card class="mx-auto" elevation="0" ref="filters">
      <v-row no-gutters>
        <v-col>
          <v-list nav dense light>
            <v-list-item prepend-icon="mdi-filter" title="Filters" nav></v-list-item>
            <v-divider></v-divider>
            <v-form ref="filter_form">
              <div v-if="comorbidityDiseaseItems.length > 0">
                <v-row>
                  <v-col cols="9">
                    <v-combobox v-model="selectedComorbidityDisease"
                                :items="comorbidityDiseaseItems"
                                label="Comorbidity/disease"
                                :rules="[rules.required]"
                                @click="$refs.filter_form.validate()"
                                multiple></v-combobox>
                  </v-col>
                  <v-col cols="3">
                    <v-btn variant="plain" @click="toggleAllNoneComorbidityDisease">All/None</v-btn>
                  </v-col>
                </v-row>
                <v-divider :vertical="true"></v-divider>
              </div>
              <div v-if="icdCodeItems.length > 0">
                <v-row>
                  <v-col cols="9">
                    <v-combobox v-model="selectedIcdCodes"
                                :items="icdCodeItems"
                                label="ICD10 codes"
                                :rules="[rules.required]"
                                :item-title="(item) => item.code + ': ' + item.description"
                                :item-value="(item) => item.code"
                                :return-object="false"
                                @click="$refs.filter_form.validate()"
                                multiple></v-combobox>
                  </v-col>
                  <v-col cols="3">
                    <v-btn variant="plain" @click="toggleAllNoneIcdCodes">All/None</v-btn>
                  </v-col>
                </v-row>
                <v-divider :vertical="true"></v-divider>
              </div>
              <div v-if="projectItems.length > 0">
                <v-row>
                  <v-col cols="9">
                    <v-combobox v-model="selectedProjects"
                                :items="projectItems"
                                label="Projects"
                                :rules="[rules.required]"
                                :item-title="(item) => item.number + ': ' + item.name"
                                :item-value="(item) => item.number"
                                :return-object="false"
                                @click="$refs.filter_form.validate()"
                                multiple></v-combobox>
                  </v-col>
                  <v-col cols="3">
                    <v-btn variant="plain" @click="toggleAllNoneProjects">All/None</v-btn>
                  </v-col>
                </v-row>
              </div>
            </v-form>
          </v-list>
        </v-col>
      </v-row>
    </v-card>
  </v-navigation-drawer>
</template>

<script>
import {defineComponent} from "vue";
import axios from "axios";
import ProgressBar from 'primevue/progressbar';
import store from '@/store';

function addItemsIfNotExists(array, items, comparator) {
    if (!Array.isArray(items)) {
        items = [items];
    }
    items.forEach(item => {
      if (!array.some(comparator(item))) {
            array.push(item);
        }
    });
    array.sort()
}

function removeItemsIfExist(array, items, comparator) {
  let arrayCopy = [...array];
  if (!Array.isArray(items)) {
    items = [items];
  }
  items.forEach(item => {
    array.forEach(a => {
      if (comparator(a)(item)) {
        let index = arrayCopy.indexOf(a);
        arrayCopy.splice(index, 1);
      }
    })
  });
  arrayCopy.sort()
  return arrayCopy
}

export default defineComponent({
  name: 'IcdCodeProjectLibraryPage',
  components: {
    ProgressBar
  },
  data: () => ({
    initialized: false,
    filter_drawer: false,
    dialog: false,
    dialogDisabled: false,
    failedSave: false,
    dialogDelete: false,
    failedDelete: false,
    dialogAddNewProject: false,
    dialogDownload: false,
    headers: [
      {
        title: 'ICD10 code',
        key: 'icd_code'
      },
      {
        title: 'ICD10 description',
        key: 'icd_description'
      },
      {
        title: 'Remarks',
        key: 'remarks'
      },
      {
        title: 'Created at',
        key: 'created_at'
      },
      {
        title: 'Updated at',
        key: 'updated_at'
      },
      {
        title: 'Last updated by',
        sortable: false,
        key: 'last_updated_by'
      },
      {
        title: 'Projects',
        sortable: false,
        key: 'projects'
      },
      {
        title: 'Actions',
        sortable: false,
        key: 'actions'
      }
    ],
    itemsPerPage: 25,
    groupBy: [{ key: 'comorbidity_disease_label', order: 'asc' }],
    icdCodeProjects: [],
    filteredIcdCodeProjects: [],
    editedIndex: -1,
    comorbidityDiseaseItems: [],
    selectedComorbidityDisease: [],
    comorbidityDiseaseSearch: null,
    icdCodeSearchItems: Array.from(store.getters.stateSocialstyrelsenGenericIcdMap, ([code, description]) => ({ code, description })),
    icdCodeItems: [],
    selectedIcdCodes: [],
    projectItems: [],
    selectedProjects: [],
    projectSearch: null,
    groupItems: [...new Set(store.getters.stateUser.groups_by_names)].sort((g1, g2) => g1.name < g2.name),
    editedItem: {},
    rules: {
      required(value) {
        if (value instanceof Array && (value.length === 0)) {
          return 'Required.';
        }
        return !!value || 'Required.';
      },
      group: [
        value => {
          if (value) return true
          return 'You must enter an owner company.'
        }
      ],
      comorbidityDisease: [
        value => {
          if (value) return true
          return 'You must enter a comorbidity/disease.'
        }
      ],
      icd_code: [
        value => {
          if (value) return true
          return 'You must enter an ICD10 code.'
        }
      ],
      projects: [
        value => {
          if (value && (value.length >= 1)) return true
          return 'You must enter at least one project.'
        }
      ],
      projectName: [
        value => {
          if (value) return true
          return 'You must enter a project name.'
        }
      ]
    }
  }),

  computed: {
    isNewEntry() {
      return this.editedIndex === -1
    },
    dialogFormTitle() {
      return this.editedIndex === -1 ? 'New Item' : 'Edit Item'
    },
    dialogFormIcdHint() {
      return this.editedIndex === -1 ? 'Select one or more ICD10 codes. If more than one code is selected, ' +
          'multiple entries will be created, with the same additional data for all selected codes.' : ''
    },
    defaultItem() {
      return {
        group: (this.groupItems.length === 1) ? this.groupItems[0].id : undefined,
        id: null,
        comorbidity_disease_label: null,
        icd_code: null,
        remarks: null,
        projects: [],
        newProjects: []
      }
    }
  },

  watch: {
    dialog(val) {
      val || this.close()
    },
    dialogDelete(val) {
      val || this.closeDelete()
    },
    dialogAddNewProject(val) {
      val || this.closeAddNewProject()
    },
    icdCodeProjects() {
      this.filterIcdCodeProjects()
    },
    selectedComorbidityDisease() {
      this.filterIcdCodeProjects()
    },
    selectedIcdCodes() {
      this.filterIcdCodeProjects()
    },
    selectedProjects() {
      this.filterIcdCodeProjects()
    }
  },

  created() {
    this.initialize()
  },

  methods: {
    async loadIcdCodeProjectLibraryData() {
      let data = axios.get('/icd-code-project-library',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {}
          });
      return data.then((response) => {
        this.icdCodeProjects = response.data;
        this.comorbidityDiseaseItems = [...new Set(this.icdCodeProjects.map(item => item.comorbidity_disease_label))].sort();
        this.selectedComorbidityDisease = [...new Set(this.icdCodeProjects.map(item => item.comorbidity_disease_label))];
        this.icdCodeItems = [...new Set(this.icdCodeProjects.map(item => item.icd_code))].sort().map(icd_code => ({
          'code': icd_code,
          'description': this.getIcdDescription(icd_code)
        }));
        this.selectedIcdCodes = [...new Set(this.icdCodeProjects.map(item => item.icd_code))];
        this.projectItems = [];
        addItemsIfNotExists(this.projectItems, this.icdCodeProjects.flatMap(item => item.projects), p1 => p2 => p1.number === p2.number);
        this.selectedProjects = [...new Set(this.icdCodeProjects.flatMap(item => item.projects).map(p => p.number))];
      });
    },
    initialize () {
      this.loadIcdCodeProjectLibraryData().then(() => {
        console.log('ICD code project library data loaded')
        this.editedItem = Object.assign({}, this.defaultItem)
        this.initialized = true
      })
    },

    getGroup(groupId) {
      let group = this.groupItems.find(g => g.id === groupId)
      return group ? Object.assign({}, group) : {
        id: groupId,
        name: 'Unknown'
      }
    },

    getIcdDescription(icd) {
      if (store.getters.stateSocialstyrelsenGenericIcdMap && store.getters.stateSocialstyrelsenGenericIcdMap.has(icd)) {
        return store.getters.stateSocialstyrelsenGenericIcdMap.get(icd)
      }
      return 'Unknown'
    },

    newProject() {
      if (!this.projectSearch || (this.projectSearch.trim() === '')) {
        return;
      }

      this.editedItem.newProjectNumber = this.projectSearch.trim();
      this.dialogAddNewProject = true;
    },

    editItem(item) {
      this.editedIndex = this.icdCodeProjects.indexOf(item)
      this.editedItem = Object.assign({}, item)
      this.editedItem.newProjects = Object.assign([], item.projects)
      this.failedSave = false
      this.dialog = true
    },

    deleteItem(item) {
      this.editedIndex = this.icdCodeProjects.indexOf(item)
      this.editedItem = Object.assign({}, item)
      this.failedDelete = false
      this.dialogDelete = true
    },

    deleteItemConfirm() {
      this.dialogDisabled = true
      let data = axios.delete('/icd-code-project-library', {
        headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
        params: {
          group: this.editedItem.group,
          icd_code_project_library_id: this.editedItem.id
        }
      });
      data.then(() => {
        this.icdCodeProjects.splice(this.editedIndex, 1)
        // Note: For some reason need to call filterIcdCodeProjects() since the watch is not triggered by splice
        this.filterIcdCodeProjects()
        this.failedDelete = false
        this.closeDelete()
      }).catch(err => {
        this.failedDelete = true
        console.log(err);
      }).finally(() => {
        this.dialogDisabled = false
      });
    },

    close() {
      this.dialog = false
      this.failedSave = false
      this.$nextTick(() => {
        this.editedItem = Object.assign({}, this.defaultItem)
        this.editedIndex = -1
      })
    },

    closeDelete() {
      this.dialogDelete = false
      this.failedDelete = false
      this.$nextTick(() => {
        this.editedItem = Object.assign({}, this.defaultItem)
        this.editedIndex = -1
      })
    },

    closeAddNewProject() {
      this.dialogAddNewProject = false
      this.$nextTick(() => {
        this.editedItem.newProjectNumber = null
        this.editedItem.newProjectName = null
      })
    },

    validateAndSaveNewProject() {
      this.dialogDisabled = true
      this.$refs.add_new_project_form.validate().then(validationResult => {
        if (validationResult.valid) {
          let newProject = {
            number: this.editedItem.newProjectNumber,
            name: this.editedItem.newProjectName
          }
          this.editedItem.newProjects =
              removeItemsIfExist(this.editedItem.newProjects, newProject, p1 => p2 => p1.number === p2.number);
          addItemsIfNotExists(this.editedItem.newProjects, newProject, p1 => p2 => p1.number === p2.number);
          this.closeAddNewProject()
        }
      }).finally(() => {
        this.dialogDisabled = false
      });
    },

    validateAndSave() {
      this.dialogDisabled = true
      this.$refs.icd_code_project_form.validate().then(validationResult => {
        if (validationResult.valid) {
          return this.save().then(() => {
            this.failedSave = false
            this.close()
          }).catch(err => {
            this.failedSave = true
            console.log(err);
          });
        }
      }).finally(() => {
        this.dialogDisabled = false
      });
    },

    save() {
      let metaData = Object.assign({}, this.editedItem, {})
      metaData.projects = this.editedItem.newProjects
      delete metaData.id
      delete metaData.group
      delete metaData.created_at
      delete metaData.updated_at
      delete metaData.last_updated_by
      delete metaData.newProjectNumber
      delete metaData.newProjectName
      delete metaData.newProjects

      const formData = new FormData();
      formData.append("group", this.editedItem.group)
      if (this.editedItem.id) {
        formData.append("icd_code_project_library_id", this.editedItem.id)
      }
      formData.append("metadata", JSON.stringify(metaData));

      let data = axios.post('/icd-code-project-library', formData, {
        headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` }
      });
      return data.then((response) => {
        if ((this.editedIndex > -1) && (response.data.length !== 1)) {
          throw new Error('Unexpected return value from update. Please reload the page and check if the update was ' +
              'successful.')
        }

        response.data.forEach(resData => {
          addItemsIfNotExists(this.comorbidityDiseaseItems, resData.comorbidity_disease_label, i1 => i2 => i1 === i2)
          addItemsIfNotExists(this.selectedComorbidityDisease, resData.comorbidity_disease_label, i1 => i2 => i1 === i2)
          addItemsIfNotExists(this.icdCodeItems, {
            'code': resData.icd_code,
            'description': this.getIcdDescription(resData.icd_code)
          }, i1 => i2 => i1.code === i2.code)
          addItemsIfNotExists(this.selectedIcdCodes, resData.icd_code, i1 => i2 => i1 === i2)
          addItemsIfNotExists(this.projectItems, resData.projects, p1 => p2 => p1.number === p2.number)
          addItemsIfNotExists(this.selectedProjects, resData.projects.map(p => p.number), p1 => p2 => p1 === p2)
        })

        if (this.editedIndex > -1) {
            Object.assign(this.icdCodeProjects[this.editedIndex], response.data[0])
        } else {
          response.data.forEach(resData => {
            this.icdCodeProjects.push(resData)
          })
          // Note: For some reason need to call filterIcdCodeProjects() since the watch is not triggered by push
          this.filterIcdCodeProjects()
        }
      });
    },

    downloadExcel() {
      this.dialogDownload = true
      this.dialogDisabled = true

      const formData = new FormData();
      this.selectedComorbidityDisease.forEach(comorbidityDisease => {
        formData.append("comorbidity_disease", comorbidityDisease)
      });
      this.selectedIcdCodes.forEach(icdCode => {
        formData.append("icd_code", icdCode)
      });
      this.selectedProjects.forEach(project => {
        formData.append("projects", project)
      });

      axios.post('/icd-code-project-library-excel', formData,
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            responseType: 'blob'
          })
          .then((response) => {
            const href = URL.createObjectURL(response.data);
            const link = document.createElement('a');
            link.href = href;
            link.setAttribute('target', '_blank');
            link.click();
            URL.revokeObjectURL(href);
          })
          .finally(() => {
            this.dialogDisabled = false
            this.dialogDownload = false
          });
    },

    toggleAllNoneComorbidityDisease() {
      if (this.selectedComorbidityDisease.length === this.comorbidityDiseaseItems.length) {
        this.selectedComorbidityDisease = [];
      } else {
        this.selectedComorbidityDisease = [...this.comorbidityDiseaseItems]
      }
    },

    toggleAllOneComorbidityDisease(comorbidityDisease) {
      if ((this.selectedComorbidityDisease.length === 1) && (this.selectedComorbidityDisease[0] === comorbidityDisease)) {
        this.selectedComorbidityDisease = [...this.comorbidityDiseaseItems]
      } else {
        this.selectedComorbidityDisease = [comorbidityDisease]
      }
    },

    toggleAllNoneIcdCodes() {
      if (this.selectedIcdCodes.length === this.icdCodeItems.length) {
        this.selectedIcdCodes = [];
      } else {
        this.selectedIcdCodes = this.icdCodeItems.map(i => i.code)
      }
    },
    
    toggleAllOneIcdCodes(icdCode) {
      if ((this.selectedIcdCodes.length === 1) && (this.selectedIcdCodes[0] === icdCode)) {
        this.selectedIcdCodes = this.icdCodeItems.map(i => i.code)
      } else {
        this.selectedIcdCodes = [icdCode]
      }
    },

    toggleAllNoneProjects() {
      if (this.selectedProjects.length === this.projectItems.length) {
        this.selectedProjects = [];
      } else {
        this.selectedProjects = this.projectItems.map(p => p.number)
      }
    },

    toggleAllOneProjects(project) {
      if ((this.selectedProjects.length === 1) && (this.selectedProjects[0] === project.number)) {
        this.selectedProjects = this.projectItems.map(p => p.number)
      } else {
        this.selectedProjects = [project.number]
      }
    },

    filterIcdCodeProjects() {
      this.filteredIcdCodeProjects = this.icdCodeProjects.filter(item => {
        return this.selectedComorbidityDisease.includes(item.comorbidity_disease_label)
            && this.selectedIcdCodes.includes(item.icd_code)
            && this.selectedProjects.some(p => item.projects.map(p => p.number).includes(p))
      })
    },

    formatTimestamp(timestamp) {
      if (!timestamp) {
        return '';
      }
      return new Date(timestamp).toLocaleString();
    }
  }
})
</script>

<style lang="scss" scoped>
.dp__theme_dark {
  --dp-background-color: #364c5b;
  --dp-text-color: #dcdcdc;
  --dp-border-color: #dcdcdc;
}
.error {
  color: indianred;
}

@layer primevue {
  .p-progressbar {
    background-color: #364c5b;
  }
}
</style>
